import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Github, Database, AlertCircle, CheckCircle2, Key, Eye, EyeOff, Save, Trash2, ChevronDown, ChevronRight, Copy, Check, MessageSquare, Globe, Trello, Music, Camera, Video, Phone, Linkedin, Twitter, Facebook, Instagram, Bot, Webhook, Hash, Zap, BrainCircuit, Lock } from 'lucide-react';
import {
  useUserIntegrations,
  useConnectGitHub,
  useDeleteIntegration,
} from '../helpers/useUserSettings';
import { Button } from './Button';
import { Badge } from './Badge';
import { Input } from './Input';
import { Skeleton } from './Skeleton';
import { Separator } from './Separator';
import styles from './UserSettingsIntegrations.module.css';
import { USER_INTEGRATIONS_QUERY_KEY } from '../helpers/useUserSettings';
import { useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { CREDENTIAL_REQUIREMENTS } from '../helpers/credentialRequirements';

export const UserSettingsIntegrations = () => {
  const { data, isLoading } = useUserIntegrations();
  const { connect: connectGitHub } = useConnectGitHub();
  const { mutate: deleteIntegration, isPending: isDeleting } = useDeleteIntegration();
  const queryClient = useQueryClient();
  const [supabaseProjectUrl, setSupabaseProjectUrl] = useState('');
  const [supabaseAnonKey, setSupabaseAnonKey] = useState('');
  const [isSavingSupabase, setIsSavingSupabase] = useState(false);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton className={styles.skeletonCard} />
        <Skeleton className={styles.skeletonCard} />
      </div>
    );
  }

  const githubIntegration = data?.integrations.find((i) => i.provider === 'github');
  const supabaseIntegration = data?.integrations.find((i) => i.provider === 'supabase');
  const supabaseMetadata = useMemo(() => {
    const raw = supabaseIntegration?.metadata;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) as Record<string, any>;
      } catch {
        return {};
      }
    }
    if (typeof raw === 'object') return raw as Record<string, any>;
    return {};
  }, [supabaseIntegration?.metadata]);
  const isSupabaseConnected = Boolean(
    supabaseIntegration &&
    (String(supabaseMetadata.projectUrl || '').trim().length > 0 ||
      String(supabaseMetadata.anonKey || '').trim().length > 0),
  );

  useEffect(() => {
    setSupabaseProjectUrl(String(supabaseMetadata.projectUrl || ''));
    setSupabaseAnonKey(String(supabaseMetadata.anonKey || ''));
  }, [supabaseMetadata.projectUrl, supabaseMetadata.anonKey]);

  const handleDisconnect = (provider: 'github' | 'gitlab' | 'atlassian' | 'supabase') => {
    if (confirm('Are you sure you want to disconnect this integration?')) {
      deleteIntegration(provider);
    }
  };

  const handleSaveSupabase = async () => {
    const projectUrl = supabaseProjectUrl.trim();
    const anonKey = supabaseAnonKey.trim();
    if (!projectUrl || !anonKey) {
      toast.error('Project URL and Anon Key are required.');
      return;
    }
    setIsSavingSupabase(true);
    try {
      const res = await fetch('/_api/integrations/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'supabase',
          metadata: { projectUrl, anonKey },
        }),
      });
      const text = await res.text();
      if (!res.ok) {
        let msg = 'Failed to save Supabase integration';
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed?.error) msg = parsed.error;
        } catch {
          // ignore parse error
        }
        throw new Error(msg);
      }
      await queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
      toast.success('Supabase integration connected.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save Supabase integration');
    } finally {
      setIsSavingSupabase(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* GitHub Section */}
      <div className={styles.integrationCard}>
        <div className={styles.cardHeader}>
          <div className={styles.providerInfo}>
            <div className={styles.iconWrapper}>
              <Github size={24} />
            </div>
            <div>
              <h3 className={styles.providerName}>GitHub</h3>
              <p className={styles.providerDesc}>
                Connect to access your repositories and sync changes.
              </p>
            </div>
          </div>
          {githubIntegration ? (
            <Badge variant="success" className={styles.badge}>
              <CheckCircle2 size={12} className={styles.badgeIcon} />
              Connected
            </Badge>
          ) : (
            <Badge variant="secondary" className={styles.badge}>
              Not Connected
            </Badge>
          )}
        </div>

        {githubIntegration ? (
          <div className={styles.connectedContent}>
            <div className={styles.userInfo}>
              <div className={styles.avatarPlaceholder}>
                {githubIntegration.providerUsername?.charAt(0).toUpperCase()}
              </div>
              <div className={styles.userDetails}>
                <span className={styles.username}>{githubIntegration.providerUsername}</span>
                <span className={styles.userId}>ID: {githubIntegration.providerUserId}</span>
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDisconnect('github')}
              disabled={isDeleting}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <div className={styles.actionRow}>
            <Button onClick={connectGitHub} size="sm">
              Connect GitHub
            </Button>
          </div>
        )}
      </div>

      <Separator className={styles.separator} />

      {/* RepoForge Promo Section */}
      <div className={`${styles.integrationCard} ${styles.promoCard}`}>
        <div className={styles.cardHeader}>
          <div className={styles.providerInfo}>
            <div className={styles.iconWrapper} style={{ color: '#22c55e' }}>
              <img
                src="/icons/repoforge-icon.png"
                alt=""
                aria-hidden="true"
                className={styles.repoForgeMark}
              />
            </div>
            <div>
              <h3 className={styles.providerName}>RepoForge Access</h3>
              <p className={styles.providerDesc}>
                Add RepoForge repository access to your existing subscription.
              </p>
            </div>
          </div>
          <Badge variant="outline" className={styles.badge}>
            Add-on: $5.00/month
          </Badge>
        </div>
        <div className={styles.promoBody}>
          <p className={styles.promoText}>
            Feature add-on only — requires an active paid subscription.
          </p>
          <Button asChild size="sm">
            <a href="/repoforge">Open RepoForge</a>
          </Button>
        </div>
      </div>

      <Separator className={styles.separator} />

      {/* Supabase Section (Placeholder) */}
      <div className={styles.integrationCard}>
        <div className={styles.cardHeader}>
          <div className={styles.providerInfo}>
            <div className={styles.iconWrapper} style={{ color: '#3ECF8E' }}>
              <Database size={24} />
            </div>
            <div>
              <h3 className={styles.providerName}>Supabase</h3>
              <p className={styles.providerDesc}>
                Connect your Supabase project for backend services.
              </p>
            </div>
          </div>
          {isSupabaseConnected ? (
            <Badge variant="success" className={styles.badge}>
              <CheckCircle2 size={12} className={styles.badgeIcon} />
              Connected
            </Badge>
          ) : (
            <Badge variant="secondary" className={styles.badge}>
              Not Connected
            </Badge>
          )}
        </div>

        <div className={styles.placeholderForm}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Project URL</label>
            <Input
              placeholder="https://your-project.supabase.co"
              value={supabaseProjectUrl}
              onChange={(e) => setSupabaseProjectUrl(e.target.value)}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Anon Key</label>
            <Input
              type="password"
              placeholder="public-anon-key"
              value={supabaseAnonKey}
              onChange={(e) => setSupabaseAnonKey(e.target.value)}
            />
          </div>
          <div className={styles.actionRow}>
            {isSupabaseConnected ? (
              <div className={styles.supabaseActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSaveSupabase}
                  disabled={isSavingSupabase}
                >
                  {isSavingSupabase ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDisconnect('supabase')}
                  disabled={isDeleting}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSaveSupabase}
                disabled={isSavingSupabase}
              >
                {isSavingSupabase ? 'Connecting...' : 'Connect Supabase'}
              </Button>
            )}
          </div>
          {!isSupabaseConnected && (
            <div className={styles.infoBox}>
              <AlertCircle size={16} />
              <span>Save your Project URL and Anon Key to complete Supabase integration.</span>
            </div>
          )}
        </div>
      </div>

      <Separator className={styles.separator} />

      {/* Bot Integrations — Dynamic credential management */}
      <BotIntegrationsSection />
    </div>
  );
};

// ─── Integration Icons ────────────────────────────────────
const INTEGRATION_ICONS: Record<string, { icon: React.ReactNode; color: string }> = {
  slack: { icon: <Hash size={16} />, color: '#E01E5A' },
  github: { icon: <Github size={16} />, color: '#fff' },
  gh: { icon: <Github size={16} />, color: '#fff' },
  tangled: { icon: <Globe size={16} />, color: '#6366f1' },
  notion: { icon: <BrainCircuit size={16} />, color: '#fff' },
  notebook: { icon: <BrainCircuit size={16} />, color: '#4285F4' },
  kalshi: { icon: <Zap size={16} />, color: '#00D4AA' },
  trello: { icon: <Trello size={16} />, color: '#0079BF' },
  discord: { icon: <MessageSquare size={16} />, color: '#5865F2' },
  promptbase: { icon: <Bot size={16} />, color: '#f97316' },
  twitter: { icon: <Twitter size={16} />, color: '#1DA1F2' },
  xai: { icon: <Zap size={16} />, color: '#fff' },
  instagram: { icon: <Instagram size={16} />, color: '#E4405F' },
  facebook: { icon: <Facebook size={16} />, color: '#1877F2' },
  linkedin: { icon: <Linkedin size={16} />, color: '#0A66C2' },
  google: { icon: <Video size={16} />, color: '#00897B' },
  teams: { icon: <Video size={16} />, color: '#6264A7' },
  microsoft: { icon: <Video size={16} />, color: '#6264A7' },
  zoom: { icon: <Video size={16} />, color: '#2D8CFF' },
  webex: { icon: <Video size={16} />, color: '#00BCF2' },
  supabase: { icon: <Database size={16} />, color: '#3ECF8E' },
  webhook: { icon: <Webhook size={16} />, color: '#f97316' },
  stripe: { icon: <Zap size={16} />, color: '#635BFF' },
  fiverr: { icon: <Globe size={16} />, color: '#1DBF73' },
  imgur: { icon: <Camera size={16} />, color: '#1BB76E' },
  exa: { icon: <Globe size={16} />, color: '#6366f1' },
  hugging: { icon: <Bot size={16} />, color: '#FFD21E' },
  browser: { icon: <Globe size={16} />, color: '#f97316' },
  tasking: { icon: <Zap size={16} />, color: '#f97316' },
  api: { icon: <Globe size={16} />, color: '#22c55e' },
};

function getIntegrationIcon(name: string): { icon: React.ReactNode; color: string } {
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(INTEGRATION_ICONS)) {
    if (lower.includes(key)) return val;
  }
  return { icon: <Lock size={16} />, color: 'var(--muted-foreground)' };
}

// ─── Bot Integrations Section ─────────────────────────────
function BotIntegrationsSection() {
  const queryClient = useQueryClient();
  const [savedCreds, setSavedCreds] = useState<Record<string, Record<string, string>>>({});
  const [editValues, setEditValues] = useState<Record<string, Record<string, string>>>({});
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedIntegrations, setExpandedIntegrations] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Custom integrations loaded from DB (merged with hardcoded CREDENTIAL_REQUIREMENTS)
  const [customIntegrations, setCustomIntegrations] = useState<Record<string, { label: string; fields: Array<{ key: string; label: string; type?: string; placeholder?: string; required?: boolean }> }>>({});

  // Load existing credentials (masked) + custom integrations
  useEffect(() => {
    (async () => {
      try {
        const [credRes, customRes] = await Promise.all([
          fetch('/_api/taskingbot/credentials', { credentials: 'include' }),
          fetch('/_api/taskingbot/custom-integrations', { credentials: 'include' }).catch(() => null),
        ]);
        if (credRes.ok) {
          const data = await credRes.json() as any;
          const creds: Record<string, Record<string, string>> = {};
          // Server returns { credentials: { name: { field: maskedValue } } }
          const credSource = data.credentials || data.integrations || {};
          for (const [name, values] of Object.entries(credSource)) {
            creds[name] = values as Record<string, string>;
          }
          setSavedCreds(creds);
        }
        // Load custom integrations and convert to CREDENTIAL_REQUIREMENTS format
        if (customRes?.ok) {
          const customData = await customRes.json() as any;
          const customs: typeof customIntegrations = {};
          for (const integ of (customData.integrations || [])) {
            customs[integ.name] = {
              label: integ.label || integ.name,
              fields: (integ.fields || []).map((f: any) => ({
                key: f.key,
                label: f.label || f.key,
                type: f.type === 'url' ? 'text' : f.type || 'password',
                placeholder: f.placeholder || `Enter ${f.label || f.key}`,
                required: f.required ?? true,
              })),
            };
          }
          setCustomIntegrations(customs);
        }
      } catch {}
      setLoaded(true);
    })();
  }, []);

  const toggleExpand = useCallback((name: string) => {
    setExpandedIntegrations(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleFieldVisibility = useCallback((fieldKey: string) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldKey)) next.delete(fieldKey);
      else next.add(fieldKey);
      return next;
    });
  }, []);

  const handleCopy = useCallback(async (fullKey: string) => {
    const [integration, key] = fullKey.split('.');
    // Copy the edit value if user typed something, otherwise copy the masked prefix
    const editVal = editValues[integration]?.[key];
    const savedVal = savedCreds[integration]?.[key];
    const valueToCopy = editVal?.trim() || savedVal || '';
    if (valueToCopy && valueToCopy !== '••••••••••••••') {
      await navigator.clipboard.writeText(valueToCopy);
      setCopiedField(fullKey);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } else {
      toast.info('Enter a new value first — saved credentials cannot be copied for security');
    }
  }, [editValues, savedCreds]);

  const updateField = useCallback((integration: string, fieldKey: string, value: string) => {
    setEditValues(prev => ({
      ...prev,
      [integration]: { ...prev[integration], [fieldKey]: value },
    }));
  }, []);

  const handleSave = useCallback(async (integrationName: string) => {
    const values = editValues[integrationName];
    if (!values || Object.values(values).every(v => !v.trim())) {
      toast.error('Enter at least one credential value');
      return;
    }
    // Filter out empty values and masked-only values
    const payload: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v.trim() && v !== '••••••••••••••') payload[k] = v.trim();
    }
    if (Object.keys(payload).length === 0) {
      toast.error('No new values to save');
      return;
    }
    setSaving(integrationName);
    try {
      const requestBody = JSON.stringify({ integration: integrationName, config: payload });
      console.log('[Integrations] Saving:', integrationName, 'payload keys:', Object.keys(payload));
      const res = await fetch('/_api/taskingbot/credentials', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: requestBody,
      });
      const responseText = await res.text();
      console.log('[Integrations] Response:', res.status, responseText);
      if (!res.ok) {
        let errMsg = 'Failed to save';
        try { errMsg = JSON.parse(responseText).error || errMsg; } catch {}
        throw new Error(errMsg);
      }
      toast.success(`${CREDENTIAL_REQUIREMENTS[integrationName]?.label || integrationName} credentials saved`);
      // Update local state
      setSavedCreds(prev => ({
        ...prev,
        [integrationName]: { ...prev[integrationName], ...Object.fromEntries(Object.keys(payload).map(k => [k, '••••••••••••••'])) },
      }));
      setEditValues(prev => ({ ...prev, [integrationName]: {} }));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  }, [editValues]);

  const handleDelete = useCallback(async (integrationName: string) => {
    if (!confirm(`Remove all ${CREDENTIAL_REQUIREMENTS[integrationName]?.label || integrationName} credentials?`)) return;
    setDeleting(integrationName);
    try {
      const res = await fetch('/_api/taskingbot/credentials', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration: integrationName, delete: true }),
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Credentials removed');
      setSavedCreds(prev => {
        const next = { ...prev };
        delete next[integrationName];
        return next;
      });
      setEditValues(prev => {
        const next = { ...prev };
        delete next[integrationName];
        return next;
      });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setDeleting(null);
    }
  }, []);

  if (!loaded) {
    return <Skeleton className={styles.skeletonCard} />;
  }

  // Merge: custom integrations + hardcoded + any stored credentials not in either list
  const knownNames = new Set([
    ...Object.keys(customIntegrations),
    ...Object.keys(CREDENTIAL_REQUIREMENTS),
  ]);
  // Build entries for stored credentials that aren't in any template
  const storedOnlyEntries: Array<[string, { label: string; fields: Array<{ key: string; label: string; type?: string }> }]> = [];
  for (const [name, values] of Object.entries(savedCreds)) {
    if (knownNames.has(name)) continue;
    // Skip internal/system credentials
    if (/^[0-9a-f]{8}[_-]|^_custom_$|^browser_cookies$/i.test(name)) continue;
    const fields = Object.keys(values).map(k => ({
      key: k,
      label: k.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
      type: /password|secret|token|key/i.test(k) ? 'password' : 'text',
    }));
    storedOnlyEntries.push([name, {
      label: name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
      fields,
    }]);
  }

  const integrationEntries = [
    ...Object.entries(customIntegrations),
    ...storedOnlyEntries,
    ...Object.entries(CREDENTIAL_REQUIREMENTS),
  ];

  return (
    <div>
      <h3 className={styles.sectionHeading}>
        <Key size={18} />
        Stored Credentials
      </h3>
      <p className={styles.sectionDesc}>
        Manage stored credentials for TaskingBot integrations. Values are encrypted with AES-256-GCM.
      </p>
      <div className={styles.credentialsList}>
        {integrationEntries.map(([name, req]) => {
          const isExpanded = expandedIntegrations.has(name);
          const hasSaved = Boolean(savedCreds[name]);
          const editVals = editValues[name] || {};

          return (
            <div key={name} className={`${styles.credentialCard} ${hasSaved ? styles.credentialCardConnected : ''}`}>
              <button className={styles.credentialCardHeader} onClick={() => toggleExpand(name)}>
                <div className={styles.credentialCardTitle}>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, background: 'color-mix(in srgb, var(--foreground) 8%, transparent)', color: getIntegrationIcon(name).color, flexShrink: 0 }}>
                    {getIntegrationIcon(name).icon}
                  </span>
                  <span className={styles.credentialName}>{req.label}</span>
                </div>
                {hasSaved ? (
                  <Badge variant="success" className={styles.badge}>
                    <CheckCircle2 size={10} className={styles.badgeIcon} />
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="secondary" className={styles.badge}>
                    Not Set
                  </Badge>
                )}
              </button>

              {isExpanded && (
                <div className={styles.credentialFields}>
                  {req.fields.map(field => {
                    const fullKey = `${name}.${field.key}`;
                    const isVisible = visibleFields.has(fullKey);
                    const savedVal = savedCreds[name]?.[field.key];
                    const editVal = editVals[field.key] ?? '';

                    return (
                      <div key={field.key} className={styles.credentialField}>
                        <div className={styles.credentialFieldLabel}>
                          <label>{field.label}</label>
                          <code className={styles.credentialFieldKey}>{field.key}</code>
                        </div>
                        <div className={styles.credentialFieldInput}>
                          <Input
                            type={field.type === 'password' && !isVisible ? 'password' : 'text'}
                            placeholder={savedVal || field.placeholder}
                            value={editVal}
                            onChange={(e) => updateField(name, field.key, e.target.value)}
                          />
                          <div className={styles.fieldActions}>
                            {field.type === 'password' && (
                              <button
                                className={styles.visibilityToggle}
                                onClick={() => toggleFieldVisibility(fullKey)}
                                title={isVisible ? 'Hide' : 'Show'}
                                type="button"
                              >
                                {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                              </button>
                            )}
                            {savedVal && (
                              <button
                                className={styles.visibilityToggle}
                                onClick={() => handleCopy(fullKey)}
                                title="Copy value"
                                type="button"
                              >
                                {copiedField === fullKey ? <Check size={14} /> : <Copy size={14} />}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className={styles.credentialActions}>
                    <Button
                      size="sm"
                      onClick={() => handleSave(name)}
                      disabled={saving === name}
                    >
                      <Save size={12} />
                      {saving === name ? 'Saving...' : 'Save'}
                    </Button>
                    {hasSaved && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(name)}
                        disabled={deleting === name}
                      >
                        <Trash2 size={12} />
                        {deleting === name ? 'Removing...' : 'Remove'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
